// src/index.ts
import { z as z2 } from "zod";

// src/pricing.ts
var DEFAULT_MARGIN_FLOOR = 0.25;
var CATEGORY_MARGIN_FLOORS = {
  "cleaning chemicals": 0.25,
  "paper products": 0.25,
  "janitorial supplies": 0.25,
  "janitorial tools": 0.25,
  "hygiene products": 0.25,
  "hygiene consumables": 0.25,
  "refuse bags & ppe": 0.25,
  "refuse/waste supplies": 0.25,
  "pine gel": 0.25,
  jik: 0.25,
  "pest control": 0.25,
  "air freshener": 0.25,
  // The single approved deviation from 25%.
  "disposable ppe": 0.22
};
function marginFloorFor(category) {
  if (!category) return DEFAULT_MARGIN_FLOOR;
  return CATEGORY_MARGIN_FLOORS[category.trim().toLowerCase()] ?? DEFAULT_MARGIN_FLOOR;
}
var PricingBasisError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "PricingBasisError";
  }
};
function toCents(value) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}
function grossMargin(cost, price, basis) {
  if (basis.cost !== basis.price) {
    throw new PricingBasisError(
      `Refusing to compute margin across mixed VAT bases (cost=${basis.cost}, price=${basis.price}). Convert both to the same basis first.`
    );
  }
  if (price === 0) return null;
  return (price - cost) / price;
}
function grossProfit(cost, price, basis) {
  if (basis.cost !== basis.price) {
    throw new PricingBasisError("Refusing to compute gross profit across mixed VAT bases.");
  }
  return toCents(price - cost);
}
function requiredPriceForMargin(cost, margin) {
  if (margin >= 1) {
    throw new RangeError("A gross margin of 100% or more is unreachable \u2014 price would be infinite.");
  }
  if (margin < 0) throw new RangeError("Margin floor cannot be negative.");
  const exact = cost / (1 - margin);
  return Math.ceil(exact * 100 - 1e-9) / 100;
}
function marginFromMarkupTrap(cost, markup) {
  const price = cost * (1 + markup);
  return price === 0 ? 0 : (price - cost) / price;
}
var MARGIN_STATUS = {
  GREEN: "GREEN",
  AMBER: "AMBER",
  RED: "RED",
  PENDING_SUPPLIER_RESEARCH: "PENDING_SUPPLIER_RESEARCH",
  PENDING_PRICING: "PENDING_PRICING",
  STRATEGIC_EXCEPTION: "STRATEGIC_EXCEPTION"
};
var AMBER_BAND = 0.05;
function assessMargin(input) {
  const floor = marginFloorFor(input.category);
  const base = {
    floor,
    effectiveFloor: floor,
    margin: null,
    profit: null,
    requiredPrice: null,
    shortfall: null,
    priceGap: null
  };
  if (input.cost === null || input.cost === void 0) {
    return {
      ...base,
      status: MARGIN_STATUS.PENDING_SUPPLIER_RESEARCH,
      reason: "No supplier cost recorded. Procurement evidence is required before margin can be assessed."
    };
  }
  if (input.price === null || input.price === void 0) {
    return {
      ...base,
      status: MARGIN_STATUS.PENDING_PRICING,
      requiredPrice: requiredPriceForMargin(input.cost, floor),
      reason: `No approved selling price. R${requiredPriceForMargin(input.cost, floor).toFixed(2)} would meet the ${(floor * 100).toFixed(0)}% floor.`
    };
  }
  const margin = grossMargin(input.cost, input.price, { cost: input.basis, price: input.basis });
  const profit = grossProfit(input.cost, input.price, { cost: input.basis, price: input.basis });
  const exceptionActive = input.exception?.status === "APPROVED" && (!input.exception.expiresAt || new Date(input.exception.expiresAt) > /* @__PURE__ */ new Date());
  const effectiveFloor = exceptionActive ? input.exception.approvedMargin : floor;
  const requiredPrice = requiredPriceForMargin(input.cost, effectiveFloor);
  const priceGap = toCents(Math.max(0, requiredPrice - input.price));
  if (margin === null) {
    return {
      ...base,
      status: MARGIN_STATUS.PENDING_PRICING,
      requiredPrice,
      reason: "Selling price is zero \u2014 margin is undefined."
    };
  }
  const meetsFloorByPrice = input.price >= requiredPrice - 5e-3;
  const shortfall = effectiveFloor - margin;
  if (exceptionActive) {
    return {
      floor,
      effectiveFloor,
      margin,
      profit,
      requiredPrice,
      shortfall: shortfall > 0 ? shortfall : null,
      priceGap: priceGap > 0 ? priceGap : null,
      status: MARGIN_STATUS.STRATEGIC_EXCEPTION,
      reason: `Operating under an approved strategic exception at ${(effectiveFloor * 100).toFixed(1)}% against a category floor of ${(floor * 100).toFixed(0)}%. Current margin ${(margin * 100).toFixed(1)}%.`
    };
  }
  if (meetsFloorByPrice) {
    return {
      floor,
      effectiveFloor,
      margin,
      profit,
      requiredPrice,
      shortfall: null,
      priceGap: null,
      status: MARGIN_STATUS.GREEN,
      reason: `Margin ${(margin * 100).toFixed(1)}% meets the ${(floor * 100).toFixed(0)}% floor.`
    };
  }
  const status = margin < 0 || shortfall > AMBER_BAND ? MARGIN_STATUS.RED : MARGIN_STATUS.AMBER;
  return {
    floor,
    effectiveFloor,
    margin,
    profit,
    requiredPrice,
    shortfall,
    priceGap,
    status,
    reason: margin < 0 ? `LOSS-MAKING: selling below cost. Margin ${(margin * 100).toFixed(1)}%. R${requiredPrice.toFixed(2)} required for the ${(floor * 100).toFixed(0)}% floor.` : `Margin ${(margin * 100).toFixed(1)}% is below the ${(floor * 100).toFixed(0)}% floor by ${(shortfall * 100).toFixed(1)} points. R${requiredPrice.toFixed(2)} required (+R${priceGap.toFixed(2)}).`
  };
}
var COMMERCIAL_ACTIONS = [
  "SOURCE_CHEAPER",
  "REPRICE",
  "CHANGE_PACK_SIZE",
  "CHANGE_BRAND",
  "RESEARCH_FURTHER",
  "STAKEHOLDER_REVIEW"
];
function recommendAction(args) {
  const { assessment, alternativeCost, marketBenchmark, currentPrice, hasMultiplePackSizes } = args;
  if (assessment.status === MARGIN_STATUS.GREEN || assessment.status === MARGIN_STATUS.STRATEGIC_EXCEPTION) {
    return null;
  }
  if (assessment.status === MARGIN_STATUS.PENDING_SUPPLIER_RESEARCH) {
    return {
      action: "RESEARCH_FURTHER",
      rationale: "No procurement evidence exists. Record at least one supplier cost before any pricing decision.",
      alternatives: ["SOURCE_CHEAPER"]
    };
  }
  if (assessment.status === MARGIN_STATUS.PENDING_PRICING) {
    return {
      action: "REPRICE",
      rationale: `No approved selling price. R${assessment.requiredPrice?.toFixed(2)} meets the floor.`,
      alternatives: ["STAKEHOLDER_REVIEW"]
    };
  }
  if (alternativeCost != null && currentPrice != null && currentPrice > 0) {
    const marginAtAlt = (currentPrice - alternativeCost) / currentPrice;
    if (marginAtAlt >= assessment.floor) {
      return {
        action: "SOURCE_CHEAPER",
        rationale: `A recorded alternative supplier cost of R${alternativeCost.toFixed(2)} reaches ${(marginAtAlt * 100).toFixed(1)}% at the current price \u2014 no price increase needed.`,
        alternatives: ["CHANGE_BRAND", "CHANGE_PACK_SIZE"]
      };
    }
  }
  if (marketBenchmark != null && assessment.requiredPrice != null) {
    if (assessment.requiredPrice <= marketBenchmark) {
      return {
        action: "REPRICE",
        rationale: `R${assessment.requiredPrice.toFixed(2)} is required for the floor and sits at or below the market benchmark of R${marketBenchmark.toFixed(2)} \u2014 the increase is defensible.`,
        alternatives: ["SOURCE_CHEAPER", "CHANGE_PACK_SIZE"]
      };
    }
    return {
      action: "SOURCE_CHEAPER",
      rationale: `R${assessment.requiredPrice.toFixed(2)} is required for the floor but the market benchmark is R${marketBenchmark.toFixed(2)} \u2014 repricing would put us above market. Procurement is the lever.`,
      alternatives: hasMultiplePackSizes ? ["CHANGE_PACK_SIZE", "CHANGE_BRAND", "STAKEHOLDER_REVIEW"] : ["CHANGE_BRAND", "STAKEHOLDER_REVIEW"]
    };
  }
  if (assessment.margin != null && assessment.margin < 0) {
    return {
      action: "STAKEHOLDER_REVIEW",
      rationale: "Selling below cost. This needs a decision now: source cheaper, reprice, or withdraw. No supplier alternative or market benchmark is on record to choose between them.",
      alternatives: ["SOURCE_CHEAPER", "REPRICE", "CHANGE_BRAND"]
    };
  }
  return {
    action: "RESEARCH_FURTHER",
    rationale: "Below floor, with no alternative supplier cost and no market benchmark on record. Gather procurement evidence before deciding between sourcing and repricing.",
    alternatives: ["SOURCE_CHEAPER", "CHANGE_PACK_SIZE", "REPRICE"]
  };
}
function computePackEconomics(input) {
  if (input.bulkVolumeLitres <= 0) throw new RangeError("Bulk volume must be greater than zero.");
  if (input.packSizeLitres <= 0) throw new RangeError("Pack size must be greater than zero.");
  const costPerLitre = input.bulkCost / input.bulkVolumeLitres;
  const rawProductCost = costPerLitre * input.packSizeLitres;
  const wastageRate = input.wastageRate ?? 0;
  const wastageCost = toCents(rawProductCost * wastageRate);
  const packagingCost = toCents(input.packagingCost ?? 0);
  const labelCost = toCents(input.labelCost ?? 0);
  const labourCost = toCents(input.labourCost ?? 0);
  const productCost = toCents(rawProductCost);
  return {
    packSizeLitres: input.packSizeLitres,
    costPerLitre: toCents(costPerLitre),
    productCost,
    packagingCost,
    labelCost,
    labourCost,
    wastageCost,
    totalUnitCost: toCents(productCost + wastageCost + packagingCost + labelCost + labourCost),
    basis: input.basis
  };
}
function modelPackSize(args) {
  const economics = computePackEconomics(args.economics);
  const assessment = assessMargin({
    category: args.category,
    cost: economics.totalUnitCost,
    price: args.currentPrice ?? null,
    basis: args.economics.basis,
    exception: args.exception
  });
  const floorToUse = assessment.effectiveFloor;
  const requiredPrice = requiredPriceForMargin(economics.totalUnitCost, floorToUse);
  return {
    economics,
    assessment,
    requiredPrice,
    pricePerLitreAtFloor: toCents(requiredPrice / args.economics.packSizeLitres),
    exceedsMarketBenchmark: args.marketBenchmark != null ? requiredPrice > args.marketBenchmark : false,
    recommendation: recommendAction({
      assessment,
      currentPrice: args.currentPrice ?? null,
      marketBenchmark: args.marketBenchmark ?? null,
      hasMultiplePackSizes: true
    })
  };
}
var SUPPORTED_PACK_SIZES_LITRES = [1, 2, 5, 10, 20, 25];

// src/delivery.ts
var DELIVERY_CONFIG = {
  freeZoneKm: 30,
  customerRatePerKm: 10,
  fuelCostPerKm: 3,
  // Placeholder pending the Gate 3 decision — see DELIVERY_OPEN_QUESTIONS.
  distanceBasis: "ONE_WAY",
  minimumDeliveryCharge: 0,
  minimumBillableKm: 0
};
var DELIVERY_OPEN_QUESTIONS = [
  {
    id: "DEL-1",
    question: "Is the per-km charge applied to one-way distance or the round trip?",
    status: "UNRESOLVED \u2014 not treated as approved",
    currentSetting: "ONE_WAY (placeholder)",
    impact: "ROUND_TRIP doubles both the customer charge and the modelled fuel cost beyond the zone. At 50 km: R200 vs R400 charged, R150 vs R300 fuel."
  },
  {
    id: "DEL-2",
    question: "What is the minimum delivery charge for any billable delivery?",
    status: "UNRESOLVED \u2014 currently disabled (0)",
    currentSetting: "0 (no floor)",
    impact: "Without a floor, a 31 km delivery bills R10 while burning R93 of modelled fuel. The first several kilometres beyond the zone are heavily loss-making."
  },
  {
    id: "DEL-3",
    question: "What is the minimum billable distance beyond the zone?",
    status: "UNRESOLVED \u2014 currently disabled (0)",
    currentSetting: "0 (no floor)",
    impact: "An alternative to DEL-2 that expresses the same protection as distance rather than rand."
  }
];
function round2(v) {
  return Math.round((v + Number.EPSILON) * 100) / 100;
}
function quoteDelivery(distanceKm, config = DELIVERY_CONFIG) {
  if (distanceKm < 0) throw new RangeError("Distance cannot be negative.");
  const multiplier = config.distanceBasis === "ROUND_TRIP" ? 2 : 1;
  const travelledKm = round2(distanceKm * multiplier);
  const withinFreeZone = distanceKm <= config.freeZoneKm;
  const rawBeyondZoneKm = round2(Math.max(0, distanceKm - config.freeZoneKm));
  const modelledFuelCost = round2(travelledKm * config.fuelCostPerKm);
  if (withinFreeZone) {
    const contribution2 = round2(0 - modelledFuelCost);
    return {
      distanceKm,
      rawBeyondZoneKm: 0,
      chargeableKm: 0,
      travelledKm,
      chargeBeforeMinimum: 0,
      customerCharge: 0,
      modelledFuelCost,
      contributionBeforeOtherVehicleCosts: contribution2,
      withinFreeZone: true,
      appliedRule: "FREE_ZONE",
      config,
      explanation: `Within the ${config.freeZoneKm} km delivery zone \u2014 no delivery charge. Modelled fuel cost R${modelledFuelCost.toFixed(2)} over ${travelledKm} km is absorbed. Contribution \u2212R${Math.abs(contribution2).toFixed(2)}.`
    };
  }
  const billedBeyondZoneKm = Math.max(rawBeyondZoneKm, config.minimumBillableKm);
  const minDistanceApplied = billedBeyondZoneKm > rawBeyondZoneKm;
  const chargeableKm = round2(billedBeyondZoneKm * multiplier);
  const chargeBeforeMinimum = round2(chargeableKm * config.customerRatePerKm);
  const minChargeApplied = chargeBeforeMinimum < config.minimumDeliveryCharge;
  const customerCharge = round2(Math.max(chargeBeforeMinimum, config.minimumDeliveryCharge));
  const contribution = round2(customerCharge - modelledFuelCost);
  const appliedRule = minChargeApplied ? "MINIMUM_CHARGE" : minDistanceApplied ? "MINIMUM_DISTANCE" : "PER_KM";
  const basisWord = config.distanceBasis === "ROUND_TRIP" ? "round trip" : "one way";
  const parts = [
    `${distanceKm} km total, ${config.freeZoneKm} km included, ${rawBeyondZoneKm} km beyond the zone.`
  ];
  if (minDistanceApplied) {
    parts.push(
      `Minimum billable distance of ${config.minimumBillableKm} km applied (raw was ${rawBeyondZoneKm} km).`
    );
  }
  parts.push(
    `${chargeableKm} km billable at R${config.customerRatePerKm}/km (${basisWord}) = R${chargeBeforeMinimum.toFixed(2)}.`
  );
  if (minChargeApplied) {
    parts.push(
      `Minimum delivery charge of R${config.minimumDeliveryCharge.toFixed(2)} applied instead.`
    );
  }
  parts.push(
    `Customer charge R${customerCharge.toFixed(2)}. Modelled fuel cost R${modelledFuelCost.toFixed(2)} over ${travelledKm} km.`,
    `Contribution ${contribution < 0 ? "\u2212" : ""}R${Math.abs(contribution).toFixed(2)} before other vehicle costs \u2014 vehicle wear, servicing, insurance, licensing and driver time are NOT modelled, so this is not profit.`
  );
  return {
    distanceKm,
    rawBeyondZoneKm,
    chargeableKm,
    travelledKm,
    chargeBeforeMinimum,
    customerCharge,
    modelledFuelCost,
    contributionBeforeOtherVehicleCosts: contribution,
    withinFreeZone: false,
    appliedRule,
    config,
    explanation: parts.join(" ")
  };
}
function deliveryBreakEvenKm(config = DELIVERY_CONFIG) {
  const multiplier = config.distanceBasis === "ROUND_TRIP" ? 2 : 1;
  void multiplier;
  if (config.customerRatePerKm <= config.fuelCostPerKm) return Infinity;
  return round2(
    config.freeZoneKm * config.customerRatePerKm / (config.customerRatePerKm - config.fuelCostPerKm)
  );
}
var DELIVERY_REVIEW_DISTANCES = [25, 30, 40, 42.86, 50, 100];
function deliveryScenarioTable(config = DELIVERY_CONFIG, distances = DELIVERY_REVIEW_DISTANCES) {
  return distances.map((d) => {
    const q = quoteDelivery(d, config);
    return {
      distanceKm: d,
      customerCharge: q.customerCharge,
      fuelCost: q.modelledFuelCost,
      contributionBeforeOtherVehicleCosts: q.contributionBeforeOtherVehicleCosts,
      appliedRule: q.appliedRule,
      withinFreeZone: q.withinFreeZone
    };
  });
}

// src/vat.ts
var DEFAULT_VAT_CONFIG = {
  isRegistered: null,
  rate: null,
  vatNumber: null,
  priceCaptureBasis: null
};
var SA_STANDARD_VAT_RATE = 0.15;
var VatNotConfiguredError = class extends Error {
  constructor(operation) {
    super(
      `Cannot ${operation}: VAT status is not configured. Mopani's VAT-registration status must be supplied by the owner \u2014 the application will not assume it.`
    );
    this.name = "VatNotConfiguredError";
  }
};
function isVatConfigured(config) {
  if (config.isRegistered === null) return false;
  if (config.isRegistered === false) return true;
  return config.rate !== null && config.priceCaptureBasis !== null;
}
function round22(v) {
  return Math.round((v + Number.EPSILON) * 100) / 100;
}
function fromExclVat(amount, config) {
  if (!isVatConfigured(config)) throw new VatNotConfiguredError("convert from excl-VAT");
  if (config.isRegistered === false) {
    return { exclVat: round22(amount), vatAmount: 0, inclVat: round22(amount), rate: 0, notRegistered: true };
  }
  const rate2 = config.rate;
  const vatAmount = round22(amount * rate2);
  return { exclVat: round22(amount), vatAmount, inclVat: round22(amount + vatAmount), rate: rate2, notRegistered: false };
}
function fromInclVat(amount, config) {
  if (!isVatConfigured(config)) throw new VatNotConfiguredError("convert from incl-VAT");
  if (config.isRegistered === false) {
    return { exclVat: round22(amount), vatAmount: 0, inclVat: round22(amount), rate: 0, notRegistered: true };
  }
  const rate2 = config.rate;
  const exclVat = round22(amount / (1 + rate2));
  return { exclVat, vatAmount: round22(amount - exclVat), inclVat: round22(amount), rate: rate2, notRegistered: false };
}
function convertBasis(amount, from, to, config) {
  if (from === to) return round22(amount);
  return from === "excl_vat" ? fromExclVat(amount, config).inclVat : fromInclVat(amount, config).exclVat;
}

// src/affiliate.ts
var L1_LADDER_BANDS = Object.freeze([
  Object.freeze({ fromOrder: 1, toOrder: 1, rate: 0.1 }),
  Object.freeze({ fromOrder: 2, toOrder: 6, rate: 0.05 }),
  Object.freeze({ fromOrder: 7, toOrder: null, rate: 0 })
]);
var L1_FLAT_BANDS = Object.freeze([
  Object.freeze({ fromOrder: 1, toOrder: null, rate: 0.06 })
]);
var L2_OVERRIDE_BANDS = Object.freeze([
  Object.freeze({ fromOrder: 1, toOrder: 1, rate: 0.025 }),
  Object.freeze({ fromOrder: 2, toOrder: 6, rate: 0.01 }),
  Object.freeze({ fromOrder: 7, toOrder: 30, rate: 5e-3 }),
  Object.freeze({ fromOrder: 31, toOrder: null, rate: 0 })
]);
var L2_COUNTER_RATIONALE = "Tier 2 counts the sub-affiliate's lifetime paid orders across all their customers, not orders by one customer. The 30-order rung is only reachable on a lifetime basis.";
var DEFAULT_AFFILIATE_CONFIG = Object.freeze({
  l1Model: "LADDER",
  l1LadderBands: L1_LADDER_BANDS,
  l1FlatBands: L1_FLAT_BANDS,
  l1Basis: "PER_CUSTOMER",
  l2Enabled: true,
  l2Bands: L2_OVERRIDE_BANDS,
  l2Basis: "PER_SUB_AFFILIATE_LIFETIME",
  commissionMetric: "GROSS_PROFIT",
  reversalPolicy: "REVERSE_ONLY",
  // 10% (L1 rung 1) + 2.5% (L2 rung 1) = 12.5%. See invariant I3.
  totalCommissionCapPctOfGp: 0.125,
  netContributionFloorPct: 0.2,
  grossMarginFloorPct: 0.25,
  attributionWindowDays: 90,
  existingCustomerExclusionMonths: 12,
  minOrderValueExVat: 5e3,
  paymentToleranceRands: 0.01
});
var MAX_SPONSOR_DEPTH = 2;
var AffiliateConfigError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "AffiliateConfigError";
  }
};
var AffiliateAttributionError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "AffiliateAttributionError";
  }
};
function rateForOrderNumber(bands, orderNumber) {
  if (!Number.isInteger(orderNumber) || orderNumber < 1) {
    throw new AffiliateConfigError(
      `Order number must be a positive integer, received ${orderNumber}. The ladder position is assigned at payment confirmation and is 1-based.`
    );
  }
  for (const band of bands) {
    const withinLower = orderNumber >= band.fromOrder;
    const withinUpper = band.toOrder === null || orderNumber <= band.toOrder;
    if (withinLower && withinUpper) return band.rate;
  }
  return 0;
}
function validateBands(bands, label) {
  if (bands.length === 0) {
    throw new AffiliateConfigError(`${label}: ladder has no bands. An empty ladder cannot be distinguished from a zero rate.`);
  }
  for (const band of bands) {
    if (!Number.isInteger(band.fromOrder) || band.fromOrder < 1) {
      throw new AffiliateConfigError(`${label}: fromOrder must be a positive integer, got ${band.fromOrder}.`);
    }
    if (band.toOrder !== null && (!Number.isInteger(band.toOrder) || band.toOrder < band.fromOrder)) {
      throw new AffiliateConfigError(`${label}: toOrder ${band.toOrder} is before fromOrder ${band.fromOrder}.`);
    }
    if (!Number.isFinite(band.rate) || band.rate < 0 || band.rate > 1) {
      throw new AffiliateConfigError(`${label}: rate ${band.rate} is outside 0..1. Rates are fractions, not percentages.`);
    }
  }
  const sorted = [...bands].sort((a, b) => a.fromOrder - b.fromOrder);
  for (let i = 1; i < sorted.length; i += 1) {
    const prev = sorted[i - 1];
    const curr = sorted[i];
    if (prev.toOrder === null || prev.toOrder >= curr.fromOrder) {
      throw new AffiliateConfigError(
        `${label}: bands overlap \u2014 [${prev.fromOrder}..${prev.toOrder ?? "\u221E"}] and [${curr.fromOrder}..${curr.toOrder ?? "\u221E"}]. Which rate applies would depend on array order.`
      );
    }
  }
}
function validateConfig(config) {
  validateBands(config.l1LadderBands, "l1LadderBands");
  validateBands(config.l1FlatBands, "l1FlatBands");
  validateBands(config.l2Bands, "l2Bands");
  if (config.totalCommissionCapPctOfGp <= 0 || config.totalCommissionCapPctOfGp > 1) {
    throw new AffiliateConfigError(
      `totalCommissionCapPctOfGp ${config.totalCommissionCapPctOfGp} is outside 0..1. A cap above 1 would permit paying out more than the order earned.`
    );
  }
  if (config.netContributionFloorPct < 0 || config.netContributionFloorPct > 1) {
    throw new AffiliateConfigError(`netContributionFloorPct ${config.netContributionFloorPct} is outside 0..1.`);
  }
  if (config.attributionWindowDays < 0) {
    throw new AffiliateConfigError("attributionWindowDays cannot be negative.");
  }
}
function maxPossibleCommissionPct(config) {
  const l1Bands = config.l1Model === "LADDER" ? config.l1LadderBands : config.l1FlatBands;
  const maxL1 = Math.max(...l1Bands.map((b) => b.rate));
  const maxL2 = config.l2Enabled ? Math.max(...config.l2Bands.map((b) => b.rate)) : 0;
  return maxL1 + maxL2;
}
function resolveSponsorChain(affiliateId, lookup, maxDepth = MAX_SPONSOR_DEPTH) {
  const chain = [];
  const seen = /* @__PURE__ */ new Set([affiliateId]);
  let current = lookup.get(affiliateId)?.sponsorId ?? null;
  while (current !== null && chain.length < maxDepth - 1) {
    if (seen.has(current)) {
      throw new AffiliateAttributionError(
        `Sponsor cycle detected at affiliate ${current}. The chain revisits an affiliate it already passed through.`
      );
    }
    seen.add(current);
    chain.push(current);
    current = lookup.get(current)?.sponsorId ?? null;
  }
  return Object.freeze(chain);
}
function assertSponsorAssignable(affiliateId, sponsorId, lookup) {
  if (affiliateId === sponsorId) {
    throw new AffiliateAttributionError(
      "An affiliate cannot sponsor themselves. Self-sponsorship would pay a second commission on the same gross profit to the same person."
    );
  }
  const seen = /* @__PURE__ */ new Set([sponsorId]);
  let current = lookup.get(sponsorId)?.sponsorId ?? null;
  while (current !== null) {
    if (current === affiliateId) {
      throw new AffiliateAttributionError(
        `Assigning ${sponsorId} as sponsor of ${affiliateId} would create a cycle \u2014 ${affiliateId} is already upstream of ${sponsorId}.`
      );
    }
    if (seen.has(current)) {
      throw new AffiliateAttributionError(`Pre-existing sponsor cycle detected at ${current}.`);
    }
    seen.add(current);
    current = lookup.get(current)?.sponsorId ?? null;
  }
}
var ATTRIBUTION_OUTCOMES = {
  ELIGIBLE: "ELIGIBLE",
  EXPIRED: "EXPIRED",
  EXISTING_CUSTOMER: "EXISTING_CUSTOMER",
  SELF_REFERRAL: "SELF_REFERRAL",
  AFFILIATE_INACTIVE: "AFFILIATE_INACTIVE",
  BELOW_MINIMUM: "BELOW_MINIMUM"
};
var MS_PER_DAY = 864e5;
function checkAttribution(input, config = DEFAULT_AFFILIATE_CONFIG) {
  const daysToAcceptance = Math.floor((input.quoteAcceptedAt.getTime() - input.referredAt.getTime()) / MS_PER_DAY);
  const deny = (outcome, reason) => Object.freeze({ outcome, eligible: false, reason, daysToAcceptance });
  if (input.sharesIdentityWithClient) {
    return deny(
      ATTRIBUTION_OUTCOMES.SELF_REFERRAL,
      "Affiliate and referred organisation share a registered contact, address or bank account."
    );
  }
  if (!input.affiliateIsActive) {
    return deny(ATTRIBUTION_OUTCOMES.AFFILIATE_INACTIVE, "Affiliate is not in an active state.");
  }
  if (daysToAcceptance < 0) {
    return deny(
      ATTRIBUTION_OUTCOMES.EXPIRED,
      `Quote was accepted ${Math.abs(daysToAcceptance)} days BEFORE the referral was logged. A referral cannot be backdated onto a quote that already existed.`
    );
  }
  if (daysToAcceptance > config.attributionWindowDays) {
    return deny(
      ATTRIBUTION_OUTCOMES.EXPIRED,
      `Quote accepted ${daysToAcceptance} days after referral, beyond the ${config.attributionWindowDays}-day window.`
    );
  }
  if (input.clientWasActiveBefore && !input.managementOverride) {
    const months = input.monthsSinceLastActivity;
    if (months === null || months < config.existingCustomerExclusionMonths) {
      return deny(
        ATTRIBUTION_OUTCOMES.EXISTING_CUSTOMER,
        `Customer was already active with Mopani within the prior ${config.existingCustomerExclusionMonths} months. Requires management approval to commission.`
      );
    }
  }
  if (input.orderValueExVat < config.minOrderValueExVat) {
    return deny(
      ATTRIBUTION_OUTCOMES.BELOW_MINIMUM,
      `Order value R${input.orderValueExVat.toFixed(2)} is below the R${config.minOrderValueExVat.toFixed(2)} minimum.`
    );
  }
  return Object.freeze({
    outcome: ATTRIBUTION_OUTCOMES.ELIGIBLE,
    eligible: true,
    reason: `Referral logged ${daysToAcceptance} days before acceptance, inside the ${config.attributionWindowDays}-day window.`,
    daysToAcceptance
  });
}
function computeCommissions(input, config = DEFAULT_AFFILIATE_CONFIG) {
  validateConfig(config);
  const gp = toCents(input.grossProfitExVat);
  const grossMarginPct = input.revenueExVat === 0 ? null : gp / input.revenueExVat;
  const emptyResult = (reasons2, withheld) => {
    const net = toCents(gp - input.deliveryCostExVat - input.otherDirectCostsExVat);
    return Object.freeze({
      accruals: Object.freeze([]),
      totalCommission: 0,
      totalCommissionPctOfGp: gp === 0 ? null : 0,
      grossProfitExVat: gp,
      grossMarginPct,
      netContribution: net,
      netContributionPct: input.revenueExVat === 0 ? null : net / input.revenueExVat,
      verdict: "REVIEW",
      verdictReasons: Object.freeze(reasons2),
      capApplied: false,
      withheldPendingPayment: withheld
    });
  };
  const shortfall = input.invoiceAmountGross - input.amountPaidGross;
  if (shortfall > config.paymentToleranceRands) {
    return emptyResult(
      [
        `No commission accrued: R${input.amountPaidGross.toFixed(2)} received against an invoice of R${input.invoiceAmountGross.toFixed(2)} (R${shortfall.toFixed(2)} outstanding). Commission triggers on cleared funds, never on a status label.`
      ],
      true
    );
  }
  if (gp <= 0) {
    return emptyResult(
      [
        `No commission accrued: gross profit is R${gp.toFixed(2)}. A commission on a loss-making order would deepen the loss.`
      ],
      false
    );
  }
  const basis = config.commissionMetric === "GROSS_PROFIT" ? gp : toCents(gp - input.deliveryCostExVat - input.otherDirectCostsExVat);
  const l1Bands = config.l1Model === "LADDER" ? config.l1LadderBands : config.l1FlatBands;
  const l1Position = input.customerPaidOrderNumber;
  const l1Rate = rateForOrderNumber(l1Bands, l1Position);
  const draft = [];
  if (l1Rate > 0) {
    draft.push({
      tier: 1,
      affiliateId: input.l1AffiliateId,
      ladderPosition: l1Position,
      rate: l1Rate,
      raw: basis * l1Rate,
      explanation: config.l1Model === "LADDER" ? `Tier 1: paid order ${l1Position} for this referred customer \u2192 ${(l1Rate * 100).toFixed(2)}% of gross profit.` : `Tier 1: flat ${(l1Rate * 100).toFixed(2)}% of gross profit (board-approved 5\u20137% band).`
    });
  }
  let l2Skip = null;
  if (!config.l2Enabled) {
    l2Skip = "Tier 2 overrides are disabled in programme configuration.";
  } else if (input.l2AffiliateId === null) {
    l2Skip = "No tier 2: the referring affiliate was not recruited by a sponsor.";
  } else if (input.l2AffiliateId === input.l1AffiliateId) {
    throw new AffiliateAttributionError(
      `Affiliate ${input.l1AffiliateId} is recorded as their own sponsor. This would pay two commissions on one gross profit to one person.`
    );
  } else {
    const l2Position = config.l2Basis === "PER_SUB_AFFILIATE_LIFETIME" ? input.subAffiliateLifetimeOrderNumber : input.customerPaidOrderNumber;
    if (l2Position === null) {
      throw new AffiliateConfigError(
        "l2Basis is PER_SUB_AFFILIATE_LIFETIME but subAffiliateLifetimeOrderNumber was not supplied. The override rate cannot be resolved without the recruit\u2019s lifetime position."
      );
    }
    const l2Rate = rateForOrderNumber(config.l2Bands, l2Position);
    if (l2Rate > 0) {
      draft.push({
        tier: 2,
        affiliateId: input.l2AffiliateId,
        ladderPosition: l2Position,
        rate: l2Rate,
        raw: basis * l2Rate,
        explanation: config.l2Basis === "PER_SUB_AFFILIATE_LIFETIME" ? `Tier 2 sponsor override: recruit's lifetime paid order ${l2Position} \u2192 ${(l2Rate * 100).toFixed(2)}% of gross profit.` : `Tier 2 sponsor override: customer paid order ${l2Position} \u2192 ${(l2Rate * 100).toFixed(2)}% of gross profit.`
      });
    } else {
      l2Skip = `Tier 2 rate is 0% at position ${l2Position} \u2014 the override ladder has tapered out.`;
    }
  }
  const rawTotal = draft.reduce((sum, d) => sum + d.raw, 0);
  const ceiling = Math.min(gp, gp * config.totalCommissionCapPctOfGp);
  let capApplied = false;
  let scale = 1;
  if (rawTotal > ceiling && rawTotal > 0) {
    capApplied = true;
    scale = ceiling / rawTotal;
  }
  const accruals = draft.map(
    (d) => Object.freeze({
      tier: d.tier,
      affiliateId: d.affiliateId,
      ladderPosition: d.ladderPosition,
      rate: d.rate,
      basisAmount: basis,
      amount: toCents(d.raw * scale),
      explanation: capApplied ? `${d.explanation} Scaled by ${(scale * 100).toFixed(4)}% to respect the ${(config.totalCommissionCapPctOfGp * 100).toFixed(2)}% total cap.` : d.explanation
    })
  );
  let totalCommission = toCents(accruals.reduce((sum, a) => sum + a.amount, 0));
  if (totalCommission > ceiling && accruals.length > 0) {
    const overshoot = toCents(totalCommission - ceiling);
    let largestIdx = 0;
    for (let i = 1; i < accruals.length; i += 1) {
      if (accruals[i].amount > accruals[largestIdx].amount) largestIdx = i;
    }
    const trimmed = toCents(accruals[largestIdx].amount - overshoot);
    accruals[largestIdx] = Object.freeze({ ...accruals[largestIdx], amount: trimmed });
    totalCommission = toCents(accruals.reduce((sum, a) => sum + a.amount, 0));
    capApplied = true;
  }
  const netContribution = toCents(gp - input.deliveryCostExVat - input.otherDirectCostsExVat - totalCommission);
  const netContributionPct = input.revenueExVat === 0 ? null : netContribution / input.revenueExVat;
  const reasons = [];
  let verdict = "APPROVE";
  if (grossMarginPct !== null && grossMarginPct < config.grossMarginFloorPct) {
    verdict = "REVIEW";
    reasons.push(
      `Gross margin ${(grossMarginPct * 100).toFixed(2)}% is below the approved ${(config.grossMarginFloorPct * 100).toFixed(2)}% floor (MS-APPROVAL-002 item 3).`
    );
  }
  if (netContributionPct !== null && netContributionPct < config.netContributionFloorPct) {
    verdict = "REVIEW";
    reasons.push(
      `Net contribution ${(netContributionPct * 100).toFixed(2)}% is below the ${(config.netContributionFloorPct * 100).toFixed(2)}% floor after commission and delivery (decision G-3).`
    );
  }
  if (capApplied) {
    verdict = "REVIEW";
    reasons.push(
      `Total commission was capped at ${(config.totalCommissionCapPctOfGp * 100).toFixed(2)}% of gross profit. On approved rates this cap cannot bind, so the configuration should be checked.`
    );
  }
  if (l2Skip !== null) reasons.push(l2Skip);
  if (verdict === "APPROVE" && reasons.length === 0) {
    reasons.push(
      `Clears both floors: ${(grossMarginPct * 100).toFixed(2)}% gross, ${(netContributionPct * 100).toFixed(2)}% net after R${totalCommission.toFixed(2)} commission.`
    );
  }
  if (totalCommission > gp) {
    throw new AffiliateConfigError(
      `INVARIANT I2 VIOLATED: total commission R${totalCommission.toFixed(2)} exceeds gross profit R${gp.toFixed(2)}.`
    );
  }
  return Object.freeze({
    accruals: Object.freeze(accruals),
    totalCommission,
    totalCommissionPctOfGp: gp === 0 ? null : totalCommission / gp,
    grossProfitExVat: gp,
    grossMarginPct,
    netContribution,
    netContributionPct,
    verdict,
    verdictReasons: Object.freeze(reasons),
    capApplied,
    withheldPendingPayment: false
  });
}
function reverseCommission(input, config = DEFAULT_AFFILIATE_CONFIG) {
  if (input.refundedFraction < 0 || input.refundedFraction > 1) {
    throw new AffiliateConfigError(`refundedFraction ${input.refundedFraction} is outside 0..1.`);
  }
  if (config.reversalPolicy === "RECALCULATE") {
    throw new AffiliateConfigError(
      "RECALCULATE is implemented for modelling only and must not be used in operation. It renumbers settled ledger rows \u2014 see decision G-2."
    );
  }
  const amount = toCents(-input.original.amount * input.refundedFraction);
  return Object.freeze({
    tier: input.original.tier,
    affiliateId: input.original.affiliateId,
    ladderPosition: input.original.ladderPosition,
    amount,
    explanation: input.refundedFraction === 1 ? `Full reversal of R${input.original.amount.toFixed(2)}. Ladder position ${input.original.ladderPosition} stays frozen (decision G-2).` : `Pro-rata reversal of ${(input.refundedFraction * 100).toFixed(2)}% \u2014 R${Math.abs(amount).toFixed(2)} of R${input.original.amount.toFixed(2)} (question M-1).`
  });
}
function projectEarnings(input, config = DEFAULT_AFFILIATE_CONFIG) {
  const settled = computeCommissions(
    { ...input, amountPaidGross: input.grossProfitExVat > 0 ? 1 : 0, invoiceAmountGross: 0 },
    config
  );
  return Object.freeze(
    settled.accruals.map(
      (a) => Object.freeze({
        tier: a.tier,
        ladderPosition: a.ladderPosition,
        rate: a.rate,
        projectedAmount: a.amount,
        isProjection: true,
        caveat: "Projection only. Nothing is earned until the invoice is paid in full and the payment clears."
      })
    )
  );
}
var AFFILIATE_OPEN_QUESTIONS = Object.freeze([
  Object.freeze({
    id: "M-1",
    question: "Commission on a partially-refunded order \u2014 reverse pro-rata on the refunded portion?",
    owner: "Vee",
    status: "SETTLED 2026-08-28: pro-rata, and idempotent \u2014 refundedFraction states how much of the ORDER was refunded, so reversal targets that proportion rather than adding to it."
  }),
  Object.freeze({
    id: "M-2",
    question: "Commission when an affiliate is deactivated mid-ladder.",
    owner: "Vee",
    status: "Earned commission stands; no new accrual. Enforced by checkAttribution()."
  }),
  Object.freeze({
    id: "M-3",
    question: "Attribution expiry \u2014 does a referral last forever?",
    owner: "Vee",
    status: "90-day quote-acceptance window implemented per board slide 10."
  }),
  Object.freeze({
    id: "M-6",
    question: "VAT on the commission itself. Depends on MOPANI's registration, not the affiliate's: a registered Mopani reclaims it as input tax and the net cost is unchanged; an unregistered Mopani absorbs a real 15%.",
    owner: "Accountant",
    status: "BLOCKED ON B-1 (vat_config.is_registered is NULL). Not modelled anywhere in this engine."
  }),
  Object.freeze({
    id: "M-7",
    question: "Minimum order value for commission eligibility.",
    owner: "Vee",
    status: "SETTLED 2026-08-28: R5,000. Below that the admin costs more than the commission is worth."
  }),
  Object.freeze({
    id: "B-1",
    question: "Is Mopani a registered VAT vendor? Compulsory above R1m taxable turnover in any 12 consecutive months; the optimistic year-one case (25 schools x 4 orders x R15,000 = R1.5m) crosses it.",
    owner: "Accountant",
    status: "UNRESOLVED. vat_config.is_registered ships NULL and conversions throw rather than assume. M-6 cannot be answered before this is."
  }),
  Object.freeze({
    id: "L2-1",
    question: "Confirm the tier-2 counter is the sub-affiliate\u2019s LIFETIME order count, not per customer.",
    owner: "Vee",
    status: "SETTLED 2026-08-28: lifetime \u2014 see L2_COUNTER_RATIONALE. Switch via l2Basis if ever revisited."
  }),
  Object.freeze({
    id: "L2-2",
    question: "Does a sponsor override survive the sub-affiliate leaving the programme?",
    owner: "Vee",
    status: "SETTLED 2026-08-28: no. The override pays for an ongoing relationship, so it stops when the recruit goes inactive. Already the behaviour."
  })
]);

// src/affiliateApi.ts
import { z } from "zod";
var uuid = z.string().uuid();
var money = z.number().finite();
var rate = z.number().min(0).max(1);
var isoDate = z.string().datetime({ offset: true });
var phone = z.string().trim().min(7).max(40).regex(/^[+0-9][0-9\s()\-]{5,}$/, "Enter a valid phone number");
var affiliateStatusSchema = z.enum([
  "pending_approval",
  "active",
  "suspended",
  "deactivated",
  "rejected"
]);
var affiliateTierClassSchema = z.enum(["A", "B", "C"]);
var referralStatusSchema = z.enum([
  "pending",
  "qualified",
  "active",
  "expired",
  "rejected",
  "disputed"
]);
var commissionStatusSchema = z.enum([
  "accrued",
  "approved",
  "paid",
  "reversed",
  "withheld",
  "disputed"
]);
var payoutStatusSchema = z.enum(["draft", "approved", "paid", "cancelled"]);
var applicationStatusSchema = z.enum([
  "new",
  "reviewing",
  "approved",
  "rejected",
  "withdrawn"
]);
var affiliateApplicationSchema = z.object({
  fullName: z.string().trim().min(2).max(120),
  email: z.string().trim().email().max(200),
  phone,
  company: z.string().trim().max(160).optional().or(z.literal("")),
  motivation: z.string().trim().max(2e3).optional().or(z.literal("")),
  /**
   * Present when the applicant arrived through an existing affiliate's referral
   * link. This is what becomes `sponsor_id` on approval, and therefore what
   * creates a tier-2 override relationship.
   */
  referredByCode: z.string().trim().max(40).optional().or(z.literal("")),
  /**
   * Honeypot. Real applicants never fill this.
   *
   * NOTE the shape: `.optional()` on an unconstrained string, NOT
   * `z.string().max(0)`. The live quote endpoint used `max(0)`, which made a
   * filled honeypot fail VALIDATION and return 400 — so the spam branch was
   * unreachable, and the 400 told any bot exactly which field caught it. The
   * request must validate successfully and then be silently discarded.
   */
  website: z.string().optional()
});
var affiliateApplicationResponseSchema = z.object({
  ok: z.literal(true),
  message: z.string(),
  reference: uuid.optional()
});
var referralCodeLookupSchema = z.object({
  valid: z.boolean(),
  /** First name only. Never the full record — this endpoint is unauthenticated. */
  affiliateFirstName: z.string().nullable()
});
var affiliatePortalDashboardSchema = z.object({
  affiliate: z.object({
    id: uuid,
    affiliateCode: z.string(),
    referralCode: z.string(),
    fullName: z.string(),
    status: affiliateStatusSchema,
    tierClass: affiliateTierClassSchema,
    joinedAt: isoDate.nullable()
  }),
  earnings: z.object({
    /** Confirmed and payable, net of any reversals. */
    payableNow: money,
    paidToDate: money,
    lifetimeTotal: money,
    /** Split so an affiliate can see what recruiting is worth to them. */
    fromOwnReferrals: money,
    fromDownline: money,
    /** Accrued but not yet approved for payout. */
    pendingApproval: money
  }),
  activity: z.object({
    liveReferrals: z.number().int(),
    activeClients: z.number().int(),
    recruitedAffiliates: z.number().int(),
    lifetimePaidOrders: z.number().int()
  }),
  /**
   * What the NEXT order from each ladder position would earn, so the incentive
   * is legible rather than mysterious. Rates only — no basis amount.
   */
  nextRates: z.object({
    tier1NextRate: rate.nullable(),
    tier1Explanation: z.string(),
    tier2NextRate: rate.nullable(),
    tier2Explanation: z.string()
  })
});
var affiliatePortalClientSchema = z.object({
  referralId: uuid,
  organisationName: z.string(),
  contactName: z.string().nullable(),
  area: z.string().nullable(),
  status: referralStatusSchema,
  referredAt: isoDate,
  attributionExpiresAt: isoDate,
  /** Ladder position for THIS client — drives the tier-1 rate. */
  paidOrderCount: z.number().int(),
  /** Total commission this relationship has earned the affiliate. */
  commissionEarned: money
  /**
   * Deliberately absent: order values, margins, what the school spends.
   * An affiliate introduces a customer; they do not get the customer's ledger.
   */
});
var affiliatePortalCommissionSchema = z.object({
  id: uuid,
  tier: z.union([z.literal(1), z.literal(2)]),
  organisationName: z.string(),
  /** For tier-2 rows: whose activity earned it. First name only. */
  earnedViaName: z.string().nullable(),
  ladderPosition: z.number().int(),
  rate,
  amount: money,
  status: commissionStatusSchema,
  earnedAt: isoDate,
  paidAt: isoDate.nullable(),
  explanation: z.string()
  /**
   * NOT included: basisAmount. It is the order's gross profit, and publishing it
   * to the person earning a percentage of it hands them Mopani's margin.
   */
});
var affiliatePortalDownlineSchema = z.object({
  id: uuid,
  /** First name and surname initial. Not email, not phone. */
  displayName: z.string(),
  status: affiliateStatusSchema,
  joinedAt: isoDate.nullable(),
  /** Their lifetime paid orders — this is what sets the sponsor's tier-2 rung. */
  lifetimePaidOrders: z.number().int(),
  /** What this recruit has earned the SPONSOR. Never what the recruit earned. */
  overrideEarned: money,
  currentOverrideRate: rate
});
var affiliatePortalPayoutSchema = z.object({
  id: uuid,
  payoutNumber: z.string(),
  periodStart: z.string(),
  periodEnd: z.string(),
  grossAmount: money,
  vatAmount: money,
  netAmount: money,
  status: payoutStatusSchema,
  paidAt: isoDate.nullable(),
  reference: z.string().nullable()
});
var referralCreateSchema = z.object({
  organisationName: z.string().trim().min(2).max(200),
  contactName: z.string().trim().max(120).optional().or(z.literal("")),
  contactPhone: phone.optional(),
  contactEmail: z.string().trim().email().max(200).optional().or(z.literal("")),
  area: z.string().trim().max(120).optional().or(z.literal("")),
  notes: z.string().trim().max(1e3).optional().or(z.literal(""))
});
var referralCreateResponseSchema = z.object({
  accepted: z.boolean(),
  referralId: uuid.nullable(),
  status: referralStatusSchema.nullable(),
  attributionExpiresAt: isoDate.nullable(),
  reason: z.string()
});
var adminAffiliateSchema = z.object({
  id: uuid,
  affiliateCode: z.string(),
  referralCode: z.string(),
  fullName: z.string(),
  company: z.string().nullable(),
  email: z.string(),
  phone: z.string().nullable(),
  status: affiliateStatusSchema,
  tierClass: affiliateTierClassSchema,
  sponsorId: uuid.nullable(),
  sponsorName: z.string().nullable(),
  sponsorLockedAt: isoDate.nullable(),
  isVatRegistered: z.boolean().nullable(),
  vatNumber: z.string().nullable(),
  bankAccountVerified: z.boolean(),
  approvedBy: uuid.nullable(),
  approvedAt: isoDate.nullable(),
  deactivatedAt: isoDate.nullable(),
  createdAt: isoDate,
  /* Aggregates from affiliate_earnings_summary */
  lifetimePaidOrders: z.number().int(),
  liveReferrals: z.number().int(),
  activeClients: z.number().int(),
  recruitedAffiliates: z.number().int(),
  tier1Total: money,
  tier2Total: money,
  lifetimeEarnings: money,
  payableNow: money,
  paidToDate: money
});
var adminAffiliateCreateSchema = z.object({
  fullName: z.string().trim().min(2).max(120),
  email: z.string().trim().email().max(200),
  phone: phone.optional(),
  company: z.string().trim().max(160).optional().or(z.literal("")),
  tierClass: affiliateTierClassSchema.default("A"),
  sponsorId: uuid.optional().nullable(),
  isVatRegistered: z.boolean().optional().nullable(),
  vatNumber: z.string().trim().max(40).optional().or(z.literal("")),
  notes: z.string().trim().max(2e3).optional().or(z.literal(""))
});
var adminAffiliateUpdateSchema = adminAffiliateCreateSchema.partial().omit({ sponsorId: true }).extend({
  status: affiliateStatusSchema.optional(),
  bankAccountVerified: z.boolean().optional()
});
var affiliateApprovalSchema = z.object({
  approve: z.boolean(),
  tierClass: affiliateTierClassSchema.optional(),
  /** Confirms the sponsor carried from the application. Null = no sponsor. */
  sponsorId: uuid.nullable().optional(),
  rejectionReason: z.string().trim().max(500).optional()
});
var adminCommissionSchema = affiliatePortalCommissionSchema.extend({
  affiliateId: uuid,
  affiliateName: z.string(),
  orderId: uuid,
  orderNumber: z.string().nullable(),
  clientId: uuid.nullable(),
  /** Gross profit the rate was applied to. Staff only. */
  basisAmount: money,
  basisMetric: z.string(),
  approvedBy: uuid.nullable(),
  approvedAt: isoDate.nullable(),
  reversesId: uuid.nullable(),
  refundedFraction: z.number().nullable()
});
var commissionApprovalSchema = z.object({
  commissionIds: z.array(uuid).min(1).max(500),
  approve: z.boolean(),
  note: z.string().trim().max(500).optional()
});
var payoutCreateSchema = z.object({
  affiliateId: uuid,
  periodStart: z.string(),
  periodEnd: z.string(),
  /** Question M-6, unresolved: does affiliate commission attract VAT? */
  vatAmount: money.default(0),
  method: z.string().trim().max(40).optional(),
  reference: z.string().trim().max(120).optional(),
  notes: z.string().trim().max(1e3).optional()
});
var commissionReversalSchema = z.object({
  commissionId: uuid,
  refundedFraction: z.number().min(0).max(1).default(1),
  reason: z.string().trim().min(3).max(500)
});
var programConfigUpdateSchema = z.object({
  config: z.record(z.string(), z.unknown()),
  changeReason: z.string().trim().min(10).max(1e3),
  approvalReference: z.string().trim().max(200).optional()
});
var orderCommissionPreviewSchema = z.object({
  orderId: uuid,
  grossProfitExVat: money,
  grossMarginPct: z.number().nullable(),
  totalCommission: money,
  netContribution: money,
  netContributionPct: z.number().nullable(),
  verdict: z.enum(["APPROVE", "REVIEW"]),
  verdictReasons: z.array(z.string()),
  accruals: z.array(
    z.object({
      tier: z.union([z.literal(1), z.literal(2)]),
      affiliateId: uuid,
      affiliateName: z.string(),
      ladderPosition: z.number().int(),
      rate,
      amount: money,
      explanation: z.string()
    })
  )
});

// src/index.ts
var optionalText = (max = 1e3) => z2.string().trim().max(max).optional().or(z2.literal(""));
var phoneSchema = z2.string().trim().min(7).max(40).regex(/^[+0-9][0-9\s()\-]{5,}$/, "Enter a valid phone number");
var uuidSchema = z2.string().uuid();
var quoteItemSchema = z2.object({
  name: z2.string().trim().min(1).max(200),
  quantity: z2.union([z2.number(), z2.string()]).optional(),
  unit: z2.string().trim().max(80).optional().or(z2.literal(""))
});
var SCHOOL_CLIENT_TYPES = ["Primary School", "Secondary School"];
var isSchoolType = (clientType) => SCHOOL_CLIENT_TYPES.includes(String(clientType ?? "").trim());
var registrationNumberSchema = z2.string().trim().min(4, "Enter the full registration number.").max(40).regex(/^[A-Za-z0-9][A-Za-z0-9\/\- ]*$/, "Use only letters, numbers, spaces, slashes and hyphens.");
var quoteRequestSchema = z2.object({
  organisationName: z2.string().trim().min(2).max(200),
  contactName: z2.string().trim().min(2).max(160),
  email: z2.string().trim().email().max(254).optional().or(z2.literal("")),
  phone: phoneSchema,
  clientType: z2.string().trim().max(100).optional().or(z2.literal("")),
  /**
   * EMIS number for a school, company registration number otherwise.
   *
   * An institution's NAME is not stable identity — "Lulekani Primary" and
   * "LULEKANI PRIMARY SCHOOL" are one school and two strings. A registration
   * number is, which is what stops the same school being registered twice under
   * two different affiliates.
   */
  registrationNumber: registrationNumberSchema,
  registrationType: z2.enum(["EMIS", "BUSINESS"]).optional(),
  area: z2.string().trim().max(160).optional().or(z2.literal("")),
  message: optionalText(1e3),
  items: z2.array(quoteItemSchema).max(100).optional().default([]),
  /**
   * Honeypot.
   *
   * DEFECT INHERITED FROM THE RECOVERED BACKEND, FIXED HERE.
   * Production declared this as `z.string().max(0)`, which REJECTS any non-empty
   * value at validation time and returns 400. That means the honeypot branch in
   * app.js — `if (data.spam_score >= 100) return res.json({ok:true})` — was
   * unreachable dead code: `spam_score` could only ever be 0, because a filled
   * honeypot never got past the validator.
   *
   * The intended behaviour is to ACCEPT the request, discard it, and return
   * success, so the bot believes it succeeded and does not retry or adapt.
   * Returning 400 tells the bot exactly which field tripped it.
   *
   * Found by the integrity harness (scripts/integrity-check.js) on 2026-08-19.
   * The field is now permissive at the schema level; the trap is enforced in the
   * route, which is where it always intended to live.
   */
  website: z2.string().max(200).optional()
});
var quoteRequestResponseSchema = z2.object({
  ok: z2.literal(true),
  message: z2.string(),
  /** Present only when the request was actually persisted (absent for honeypot hits). */
  reference: uuidSchema.optional()
});
var contactRequestSchema = z2.object({
  fullName: z2.string().trim().min(2).max(160),
  email: z2.string().trim().email().max(254),
  phone: phoneSchema.optional().or(z2.literal("")),
  message: z2.string().trim().min(5).max(2e3),
  /** Honeypot — permissive at schema level, trapped in the route. See quoteRequestSchema. */
  website: z2.string().max(200).optional()
});
var contactResponseSchema = z2.object({
  ok: z2.literal(true),
  message: z2.string(),
  reference: uuidSchema.optional()
});
var productCategorySchema = z2.object({
  slug: z2.string(),
  name: z2.string(),
  count: z2.number().int().nonnegative()
});
var publicProductSchema = z2.object({
  id: uuidSchema,
  sku: z2.string().nullable(),
  name: z2.string(),
  category: z2.string().nullable(),
  unit: z2.string().nullable(),
  description: z2.string().nullable(),
  imageUrl: z2.string().nullable()
});
var publicProductListSchema = z2.object({
  data: z2.array(publicProductSchema),
  categories: z2.array(productCategorySchema),
  total: z2.number().int().nonnegative()
});
var loginSchema = z2.object({
  email: z2.string().trim().min(1).max(254).optional(),
  username: z2.string().trim().min(1).max(80).optional(),
  identifier: z2.string().trim().min(1).max(254).optional(),
  password: z2.string().trim().min(8).max(200)
}).refine((d) => d.email || d.username || d.identifier, {
  message: "Email, username, or identifier is required",
  path: ["identifier"]
});
var roleNameSchema = z2.enum([
  "Owner / Admin",
  "Sales",
  "Operations",
  "Finance",
  "Viewer"
]);
var sessionUserSchema = z2.object({
  id: uuidSchema,
  username: z2.string().nullable(),
  email: z2.string().nullable(),
  fullName: z2.string().nullable().optional(),
  role: z2.string().nullable(),
  isActive: z2.boolean().optional(),
  /**
   * May reset ANOTHER person's password. A capability, not a role — being an
   * Owner / Admin does not confer it. The UI uses it to decide what to show;
   * the server re-checks it on every reset, so hiding a control is a courtesy
   * and never the control itself.
   */
  canResetPasswords: z2.boolean().optional(),
  /**
   * May read the activity log. Also a capability rather than a role — reading a
   * record of what everyone did is its own decision, and all four directors
   * hold Owner / Admin.
   */
  canViewActivityLog: z2.boolean().optional(),
  /**
   * Every permission this person's role holds, e.g. `quotes:write`.
   *
   * Sent so the admin can hide what someone cannot use rather than presenting
   * buttons that fail. It is NOT the access control: the server re-derives the
   * same list from the database on every request, so an edited response buys
   * nothing but a visible button that still returns 403.
   */
  permissions: z2.array(z2.string()).optional()
});
var loginResponseSchema = z2.object({
  ok: z2.literal(true),
  user: sessionUserSchema
});
var meResponseSchema = z2.object({ user: sessionUserSchema });
var paginationQuerySchema = z2.object({
  page: z2.coerce.number().int().min(1).default(1),
  pageSize: z2.coerce.number().int().min(1).max(100).default(25),
  sort: z2.string().max(40).optional(),
  order: z2.enum(["asc", "desc"]).default("desc"),
  q: z2.string().trim().max(120).optional(),
  status: z2.string().trim().max(40).optional()
});
var pageMetaSchema = z2.object({
  page: z2.number().int(),
  pageSize: z2.number().int(),
  total: z2.number().int(),
  totalPages: z2.number().int()
});
var listEnvelope = (item) => z2.object({ data: z2.array(item), meta: pageMetaSchema });
var quoteRequestStatusSchema = z2.enum([
  "new",
  "reviewing",
  "quoted",
  "converted",
  "closed"
]);
var adminQuoteRequestSchema = z2.object({
  id: uuidSchema,
  organisationName: z2.string(),
  contactName: z2.string(),
  email: z2.string().nullable(),
  phone: z2.string(),
  clientType: z2.string().nullable(),
  area: z2.string().nullable(),
  message: z2.string().nullable(),
  items: z2.array(quoteItemSchema),
  status: quoteRequestStatusSchema,
  source: z2.string(),
  createdAt: z2.string(),
  updatedAt: z2.string()
});
var adminQuoteRequestUpdateSchema = z2.object({
  status: quoteRequestStatusSchema.optional(),
  message: optionalText(2e3),
  clientType: z2.string().trim().max(100).optional(),
  area: z2.string().trim().max(160).optional()
});
var internalNoteSchema = z2.object({
  id: uuidSchema,
  note: z2.string(),
  createdBy: uuidSchema.nullable(),
  createdAt: z2.string()
});
var internalNoteCreateSchema = z2.object({
  note: z2.string().trim().min(1).max(4e3)
});
var dashboardSchema = z2.object({
  quoteRequests: z2.number().int(),
  quoteRequestsNew: z2.number().int(),
  clients: z2.number().int(),
  quotes: z2.number().int(),
  orders: z2.number().int(),
  payments: z2.number().int(),
  deliveries: z2.number().int(),
  products: z2.number().int()
});
var orderStatusSchema = z2.enum([
  "awaiting_invoice",
  "invoiced",
  "awaiting_payment",
  "payment_cleared",
  "procurement",
  "ready_for_delivery",
  "out_for_delivery",
  "delivered",
  "pod_received",
  "closed",
  "cancelled"
]);
var ORDER_TRANSITIONS = {
  awaiting_invoice: ["invoiced", "cancelled"],
  invoiced: ["awaiting_payment", "cancelled"],
  awaiting_payment: ["payment_cleared", "cancelled"],
  // The load-bearing rule: procurement is reachable ONLY from payment_cleared.
  payment_cleared: ["procurement", "cancelled"],
  procurement: ["ready_for_delivery", "cancelled"],
  ready_for_delivery: ["out_for_delivery", "cancelled"],
  out_for_delivery: ["delivered", "cancelled"],
  delivered: ["pod_received"],
  pod_received: ["closed"],
  closed: [],
  cancelled: []
};
function canTransition(from, to) {
  return ORDER_TRANSITIONS[from]?.includes(to) ?? false;
}
var orderTransitionSchema = z2.object({
  to: orderStatusSchema,
  note: optionalText(1e3)
});
var invoiceStatusSchema = z2.enum([
  "draft",
  "issued",
  "paid",
  "cancelled"
]);
var invoiceItemSchema = z2.object({
  id: uuidSchema.optional(),
  productId: uuidSchema.nullable().optional(),
  description: z2.string().trim().min(1).max(300),
  quantity: z2.coerce.number().positive(),
  unit: z2.string().trim().max(80).nullable().optional(),
  unitPrice: z2.coerce.number().nonnegative(),
  lineTotal: z2.coerce.number().nonnegative().optional()
});
var invoiceSchema = z2.object({
  id: uuidSchema,
  invoiceNumber: z2.string(),
  quoteId: uuidSchema.nullable(),
  orderId: uuidSchema.nullable(),
  clientId: uuidSchema.nullable(),
  status: invoiceStatusSchema,
  issuedAt: z2.string().nullable(),
  dueAt: z2.string().nullable(),
  /** Stored as supplied. No VAT rate is computed by the application — see contract doc. */
  subtotal: z2.number(),
  taxTotal: z2.number(),
  total: z2.number(),
  notes: z2.string().nullable(),
  items: z2.array(invoiceItemSchema),
  createdAt: z2.string(),
  updatedAt: z2.string()
});
var invoiceCreateSchema = z2.object({
  quoteId: uuidSchema.optional(),
  orderId: uuidSchema.optional(),
  clientId: uuidSchema.optional(),
  dueAt: z2.string().optional(),
  notes: optionalText(2e3),
  items: z2.array(invoiceItemSchema).min(1).max(200),
  subtotal: z2.coerce.number().nonnegative(),
  taxTotal: z2.coerce.number().nonnegative(),
  total: z2.coerce.number().nonnegative()
});
var apiErrorSchema = z2.object({
  error: z2.string(),
  details: z2.record(z2.string(), z2.array(z2.string())).optional()
});
var BUSINESS = {
  name: "Mopani Suppliers",
  contactName: "Ndzalama Makhubele",
  contactKnownAs: "Ndzali",
  contactTitle: "Sales / Client Contact",
  /** AUTHORITATIVE. 0729655486 was a mockup value — never use it. */
  phone: "0639960861",
  phoneDisplay: "063 996 0861",
  whatsapp: "27639960861",
  whatsappUrl: "https://wa.me/27639960861",
  email: "info@mopani-suppliers.store",
  region: "Mopani District, Limpopo",
  town: "Phalaborwa",
  province: "Limpopo",
  postalCode: "1390",
  publicUrl: "https://mopani-suppliers.store",
  adminUrl: "https://admin.mopani-suppliers.store",
  apiUrl: "https://api.mopani-suppliers.store",
  hours: [
    { days: "Mon - Fri", open: "08:00", close: "17:00" },
    { days: "Saturday", open: "09:00", close: "13:00" },
    { days: "Sunday", open: null, close: null }
  ],
  serviceAreas: [
    "Phalaborwa Town",
    "Namakgale",
    "Lulekani",
    "Makhushane",
    "Mashishimale",
    "Gravelotte",
    "Mica",
    "Selwane",
    "Ba-Phalaborwa Municipality Area",
    "Wider Mopani District (On Request)"
  ]
};
export {
  AFFILIATE_OPEN_QUESTIONS,
  AMBER_BAND,
  ATTRIBUTION_OUTCOMES,
  AffiliateAttributionError,
  AffiliateConfigError,
  BUSINESS,
  CATEGORY_MARGIN_FLOORS,
  COMMERCIAL_ACTIONS,
  DEFAULT_AFFILIATE_CONFIG,
  DEFAULT_MARGIN_FLOOR,
  DEFAULT_VAT_CONFIG,
  DELIVERY_CONFIG,
  DELIVERY_OPEN_QUESTIONS,
  DELIVERY_REVIEW_DISTANCES,
  L1_FLAT_BANDS,
  L1_LADDER_BANDS,
  L2_COUNTER_RATIONALE,
  L2_OVERRIDE_BANDS,
  MARGIN_STATUS,
  MAX_SPONSOR_DEPTH,
  ORDER_TRANSITIONS,
  PricingBasisError,
  SA_STANDARD_VAT_RATE,
  SCHOOL_CLIENT_TYPES,
  SUPPORTED_PACK_SIZES_LITRES,
  VatNotConfiguredError,
  adminAffiliateCreateSchema,
  adminAffiliateSchema,
  adminAffiliateUpdateSchema,
  adminCommissionSchema,
  adminQuoteRequestSchema,
  adminQuoteRequestUpdateSchema,
  affiliateApplicationResponseSchema,
  affiliateApplicationSchema,
  affiliateApprovalSchema,
  affiliatePortalClientSchema,
  affiliatePortalCommissionSchema,
  affiliatePortalDashboardSchema,
  affiliatePortalDownlineSchema,
  affiliatePortalPayoutSchema,
  affiliateStatusSchema,
  affiliateTierClassSchema,
  apiErrorSchema,
  applicationStatusSchema,
  assertSponsorAssignable,
  assessMargin,
  canTransition,
  checkAttribution,
  commissionApprovalSchema,
  commissionReversalSchema,
  commissionStatusSchema,
  computeCommissions,
  computePackEconomics,
  contactRequestSchema,
  contactResponseSchema,
  convertBasis,
  dashboardSchema,
  deliveryBreakEvenKm,
  deliveryScenarioTable,
  fromExclVat,
  fromInclVat,
  grossMargin,
  grossProfit,
  internalNoteCreateSchema,
  internalNoteSchema,
  invoiceCreateSchema,
  invoiceItemSchema,
  invoiceSchema,
  invoiceStatusSchema,
  isSchoolType,
  isVatConfigured,
  listEnvelope,
  loginResponseSchema,
  loginSchema,
  marginFloorFor,
  marginFromMarkupTrap,
  maxPossibleCommissionPct,
  meResponseSchema,
  modelPackSize,
  orderCommissionPreviewSchema,
  orderStatusSchema,
  orderTransitionSchema,
  pageMetaSchema,
  paginationQuerySchema,
  payoutCreateSchema,
  payoutStatusSchema,
  phoneSchema,
  productCategorySchema,
  programConfigUpdateSchema,
  projectEarnings,
  publicProductListSchema,
  publicProductSchema,
  quoteDelivery,
  quoteItemSchema,
  quoteRequestResponseSchema,
  quoteRequestSchema,
  quoteRequestStatusSchema,
  rateForOrderNumber,
  recommendAction,
  referralCodeLookupSchema,
  referralCreateResponseSchema,
  referralCreateSchema,
  referralStatusSchema,
  registrationNumberSchema,
  requiredPriceForMargin,
  resolveSponsorChain,
  reverseCommission,
  roleNameSchema,
  sessionUserSchema,
  toCents,
  uuidSchema,
  validateBands,
  validateConfig
};
